<template>
  <iDropdownMenu
    :items="variants"
    :show-dropdown="displayDropdown"
    :max-height="maxHeight"
    :overlay-position="overlayPosition"
    :z-index="zIndex"
    @close="() => { displayDropdown = false }"
    @click="handleSelectPlatform"
  >
    <template v-if="!useOwnDisplayDropdown" #trigger>
      <iColumn @click="showDropdown = !showDropdown">
        <slot name="trigger">
          <iButton
            variant="secondary"
            width="hug"
          >
            Create Story
          </iButton>
        </slot>
      </iColumn>
    </template>
  </iDropdownMenu>
</template>

<script>
import { useSocialNetworksStore } from "@/stores/social-networks-store";
import { socialNetworkConnectionStatus } from "@/enums/social-network-enums";
import { useActivePublicationFeedStore } from "SmartFeed/stores/feed/active-publication-feed-store";
import { SOCIAL_NETWORK_LINKS } from "&/constants/socials-constants";
import { PUBLISHING_PLATFORM_TYPE } from "%/constants/story-constants";
import { MenuConstants } from "@bloglovin/vue-component-library";
import { mapActions } from "pinia";
import { useStoryVariantStore } from "@/stores/story-variant-store";
import { useDraftPublicationFeedStore } from "SmartFeed/stores/feed/draft-publication-feed-store";
import SmartFeedEngine from "@/smart-feed/smart-feed-engine";

export default {
  name: "CreateVariantDropdown",
  props: {
    storyId: {
      type: [Number, String],
      required: false,
      default: "",
    },
    canSkip: {
      type: Boolean,
      required: false,
      default: false,
    },
    overlayPosition: {
      type: String,
      required: false,
      default: "top-right",
    },
    useOwnDisplayDropdown: {
      type: Boolean,
      default: false,
    },
    propShowDropdown: {
      type: Boolean,
      default: false,
    },
    maxHeight: {
      type: Number,
      default: 500,
    },
    zIndex: {
      type: Number,
      required: false,
      default: 2,
    },
  },
  emits: ["assign", "open", "close"],
  data() {
    return {
      showDropdown: false,
    };
  },
  computed: {
    displayDropdown: {
      get() {
        return this.useOwnDisplayDropdown ? this.propShowDropdown : this.showDropdown;
      },
      set(value) {
        if (this.useOwnDisplayDropdown) {
          this.showDropdown = value;
        } else {
          this.showDropdown = value;
        }
      },
    },
    publications() {
      const publicationEntityStore = SmartFeedEngine.getEntityPiniaStore("publication");
      return Object.values(publicationEntityStore().entities);
    },
    socialNetworks() {
      return useSocialNetworksStore().socialNetworks.filter(network => [
        socialNetworkConnectionStatus.CONNECTED,
        socialNetworkConnectionStatus.MANUAL_CONNECTION,
      ].includes(network.status));
    },
    variants() {
      const items = [];

      this.publications.forEach(publication => {
        if (publication.publicationId !== this.storyId) {
          items.push({
            name: publication.name,
            label: publication.name,
            subLabel: "Publication",
            icon: "logo",
            actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
            actionValue: `${PUBLISHING_PLATFORM_TYPE.PUBLICATION}.${publication.publicationId}`,
          });
        }
      });

      this.publications.forEach(publication => {
        if (publication.publicationId !== this.storyId) {
          items.push({
            name: `${publication.name}`,
            label: `${publication.name}`,
            subLabel: "Newsletter",
            icon: "envelope-open-text",
            actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
            actionValue: `${PUBLISHING_PLATFORM_TYPE.EMAIL}.${publication.publicationId}`,
          });
        }
      });

      items.push({
        name: "syndicationMarketplace",
        label: "Syndication Marketplace",
        subLabel: "Marketplace",
        icon: "shopping-bag",
        actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
        actionValue: `${PUBLISHING_PLATFORM_TYPE.SYNDICATION_MARKETPLACE}.${this.storyId}`,
      });

      this.socialNetworks.forEach(network => {
        const details = this.getSocialNetworkDetails(network.social_network_name);
        if (!details) {
          return;
        }

        items.push({
          name: network.social_network_name,
          label: network.social_network_name,
          subLabel: details.label,
          icon: details.icon,
          actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
          actionValue: `${PUBLISHING_PLATFORM_TYPE.SOCIAL_NETWORK}.${network.social_network_account_id}`,
        });
      });

      if (this.canSkip) {
        items.push({
          name: "not_now",
          label: "Not Now",
          actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
          actionValue: "skip",
        });
      }

      return items;
    },
  },
  watch: {
    showDropdown(newValue) {
      if (newValue) {
        this.$emit("open");
      } else {
        this.$emit("close");
      }
    },
  },
  mounted() {
    useActivePublicationFeedStore().getSmartFeed();
    useDraftPublicationFeedStore().getSmartFeed();
  },
  methods: {
    ...mapActions(useStoryVariantStore, ["getVariantTypeBySocialNetworkName"]),
    getSocialNetworkDetails(name) {
      return SOCIAL_NETWORK_LINKS.find(network => network.name === name);
    },
    handleSelectPlatform(actionKey) {
      const actionValues = actionKey.split(".");
      this.$emit("assign", this.getEntity(actionValues[0], actionValues.at(-1)));
    },
    getEntity(type, id) {
      const publication = this.publications.find(publication => publication.publicationId === parseInt(id));
      const network = this.socialNetworks.find(socialNetwork => socialNetwork.social_network_account_id ===
          parseInt(id));

      switch (type) {
        case PUBLISHING_PLATFORM_TYPE.PUBLICATION:
          return {
            publishing_platform_id: publication.publicationId,
            publishing_platform_type: PUBLISHING_PLATFORM_TYPE.PUBLICATION,
            publishing_platform_name: publication.name,
            variant_type: "publication_post",
          };
        case PUBLISHING_PLATFORM_TYPE.SOCIAL_NETWORK:
          return {
            publishing_platform_id: network.social_network_account_id,
            publishing_platform_type: PUBLISHING_PLATFORM_TYPE.SOCIAL_NETWORK,
            publishing_platform_name: network.social_network_name,
            variant_type: this.getVariantTypeBySocialNetworkName(network.social_network_name),
          };
        case PUBLISHING_PLATFORM_TYPE.SYNDICATION_MARKETPLACE:
          return {
            publishing_platform_id: 0,
            publishing_platform_type: PUBLISHING_PLATFORM_TYPE.SYNDICATION_MARKETPLACE,
            publishing_platform_name: "Syndication Marketplace",
            variant_type: "publication_post",
          };
        case PUBLISHING_PLATFORM_TYPE.EMAIL:
          return {
            publishing_platform_id: publication.publicationId,
            publishing_platform_type: PUBLISHING_PLATFORM_TYPE.PUBLICATION,
            publishing_platform_name: publication.name,
            variant_type: "email",
          };
        default:
          return {};
      }
    },
  },
};
</script>

<style lang="scss" scoped>

</style>
